import { FilePenIcon, SearchXIcon } from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  useSubscriptions,
  useSubscriptionsSearchParams,
} from "@/hooks"

import { AppPagination } from "../commons/app-pagination"
import { AppSkeletonTable } from "../commons/app-skeleton-table"
import { SubscriptionsTableRow } from "./table-row"

export const SubscriptionsTable = () => {
  const { t } = useTranslation()
  const { params } = useSubscriptionsSearchParams()
  const { data, isFetching } = useSubscriptions(
    params,
    Boolean(params.refetch)
  )
  const pagination = data?.pagination
  const subscriptions = data?.subscriptions || []
  const hasSubscriptions = subscriptions.length > 0
  const paginationData = {
    currentPage: pagination?.currentPage || 1,
    total: pagination?.totalResults || 0,
    totalPages: pagination?.totalPages || 0,
  }

  if (isFetching) return <AppSkeletonTable columns={9} />

  if (!params.refetch) {
    return (
      <div className="mt-4 flex flex-1 flex-col items-center justify-center gap-4 text-muted-foreground">
        <FilePenIcon size={48} strokeWidth={1.5} />
        <p className="text-center">
          {t("pages.subscriptions.searchToView")}
        </p>
      </div>
    )
  }

  if (!hasSubscriptions) {
    return (
      <div className="mt-4 flex flex-1 flex-col items-center justify-center gap-4 text-muted-foreground">
        <SearchXIcon size={48} strokeWidth={1.5} />
        <div className="text-center">
          <p className="font-medium">
            {t("pages.subscriptions.noSubscriptionsFound")}
          </p>
          <p className="text-sm">
            {t("pages.subscriptions.tryDifferentFilters")}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="relative overflow-y-auto overflow-x-hidden rounded-md border border-border">
        <Table
          className="relative h-10 w-full overflow-clip rounded-md border-border"
          divClassname="max-h-[68vh] overflow-y-scroll scrollbar"
        >
          <TableHeader className="sticky top-0 z-20 h-10 w-full bg-background outline outline-1 outline-border">
            <TableRow>
              <TableHead className="w-[140px]">
                {t("subscriptions.table.identification")}
              </TableHead>
              <TableHead className="hidden w-[240px] lg:table-cell">
                {t("subscriptions.table.customer")}
              </TableHead>
              <TableHead className="hidden w-[80px] text-center xl:table-cell">
                {t("subscriptions.table.cycles")}
              </TableHead>
              <TableHead className="hidden w-[140px] lg:table-cell">
                {t("subscriptions.table.periodicity")}
              </TableHead>
              <TableHead className="hidden w-[140px] lg:table-cell">
                {t("subscriptions.table.paymentMethod")}
              </TableHead>
              <TableHead className="hidden w-[100px] text-end sm:table-cell">
                {t("subscriptions.table.amount")}
              </TableHead>
              <TableHead className="hidden w-[140px] lg:table-cell">
                {t("subscriptions.table.startDate")}
              </TableHead>
              <TableHead className="hidden w-[120px] text-center md:table-cell">
                {t("subscriptions.table.actions")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {subscriptions.map((subscription) => (
              <SubscriptionsTableRow
                key={subscription.id || subscription.code}
                subscription={subscription}
                filteredPaymentMethod={params.paymentMethod}
              />
            ))}
          </TableBody>
        </Table>
      </div>
      <AppPagination pagination={paginationData} />
    </div>
  )
}
